/**
 * Geometry utility functions for geospatial calculations
 */

import { EdgeParams } from './parameterSelection'
import { RayWalkResult, walkDirectionDeVerbose } from './rayWalking'

/**
 * Computes the Haversine distance between two coordinate points in meters
 * Used for building the hole ROI from the hole's length
 *
 * @param lat1 - Latitude of first point in degrees
 * @param lon1 - Longitude of first point in degrees
 * @param lat2 - Latitude of second point in degrees
 * @param lon2 - Longitude of second point in degrees
 * @returns Distance in meters
 */
export function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000.0 // Earth radius in meters
  const p1 = toRadians(lat1)
  const p2 = toRadians(lat2)
  const dphi = toRadians(lat2 - lat1)
  const dlmb = toRadians(lon2 - lon1)

  const a = Math.sin(dphi / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dlmb / 2) ** 2

  return 2 * R * Math.asin(Math.sqrt(a))
}

/**
 * Converts degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

/**
 * Computes the angle formed by three points (left, mid, right)
 * Used in inward dent candidate detection, Pass 2 selection metric,
 * Pass 3 debug metric (spike sharpness), and Pass 4 acceptance/diagnostic
 *
 * @param pLeft - Left point [x, y]
 * @param pMid - Middle point [x, y]
 * @param pRight - Right point [x, y]
 * @returns Angle in degrees, or null if degenerate
 */
export function angleDeg(pLeft: [number, number], pMid: [number, number], pRight: [number, number]): number | null {
  const a = [pLeft[0] - pMid[0], pLeft[1] - pMid[1]]
  const b = [pRight[0] - pMid[0], pRight[1] - pMid[1]]

  const na = Math.sqrt(a[0] * a[0] + a[1] * a[1])
  const nb = Math.sqrt(b[0] * b[0] + b[1] * b[1])

  if (na < 1e-6 || nb < 1e-6) {
    return null
  }

  const dot = a[0] * b[0] + a[1] * b[1]
  let c = dot / (na * nb)
  c = Math.max(-1.0, Math.min(1.0, c))

  return (Math.acos(c) * 180) / Math.PI
}

/**
 * Determines if a point represents an inward dent relative to center
 * Used in Pass 4 candidate gating and many logs
 *
 * @param ax - Anchor x coordinate (center)
 * @param ay - Anchor y coordinate (center)
 * @param pLeft - Left neighbor point [x, y]
 * @param pMid - Middle point to test [x, y]
 * @param pRight - Right neighbor point [x, y]
 * @param marginPx - Margin in pixels (bigger = fewer dents)
 * @returns True if the point is inward relative to center
 */
export function isInwardByCenter(
  ax: number,
  ay: number,
  pLeft: [number, number],
  pMid: [number, number],
  pRight: [number, number],
  marginPx: number = 2.0,
): boolean {
  // Midpoint of chord between neighbors
  const mx = 0.5 * (pLeft[0] + pRight[0])
  const my = 0.5 * (pLeft[1] + pRight[1])

  const dMid = Math.hypot(mx - ax, my - ay)
  const dP = Math.hypot(pMid[0] - ax, pMid[1] - ay)

  return dP < dMid - marginPx
}

/**
 * Computes Euclidean distance between two points
 *
 * @param x1 - First point x coordinate
 * @param y1 - First point y coordinate
 * @param x2 - Second point x coordinate
 * @param y2 - Second point y coordinate
 * @returns Distance
 */
export function euclideanDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.hypot(x2 - x1, y2 - y1)
}

/** Euclidean distance in LAB space (ΔE76) */
export function de76(lab1: ArrayLike<number>, lab2: ArrayLike<number>): number {
  const dL = lab1[0] - lab2[0]
  const da = lab1[1] - lab2[1]
  const db = lab1[2] - lab2[2]
  return Math.hypot(dL, da, db)
}

/**
 * Returns the two neighbor labels for a given direction in a circular direction system.
 */
export function neighborNames(name: string, totalDirections: number): [string, string] {
  const i = parseInt(name, 10)
  const safeTotalDirections = Math.max(3, Math.round(totalDirections))
  const left = String(((i - 2 + safeTotalDirections) % safeTotalDirections) + 1)
  const right = String((i % safeTotalDirections) + 1)
  return [left, right]
}

/**
 * Backward-compatible helper for the historic 16-direction system.
 */
export function neighborNames16(name: string): [string, string] {
  return neighborNames(name, 16)
}

// // ─────────────────────────────────────────────
// // Adaptive Ray Runner (retries with smaller steps)
// // ─────────────────────────────────────────────

export function runOneDirWithAdaptive(
  label: string,
  v: [number, number],
  params: EdgeParams,
  axPx: number,
  ayPx: number,
  radiusPx: number,
  samplingRadiusPx: number,
  patchHalf: number,
  stepPx: number,
  roiRgba: ImageData,
  W: number,
  H: number,
  validMask: Uint8Array | null,
  minPix: number,
  mpp: number,
): RayWalkResult | null {
  function runWith(scale: number, forceFirst: boolean): RayWalkResult | null {
    const p = { ...params }
    p.stepFrac *= scale
    p.dStartFrac *= scale
    return walkDirectionDeVerbose(
      [axPx, ayPx],
      v,
      radiusPx,
      samplingRadiusPx,
      patchHalf,
      stepPx,
      roiRgba,
      W,
      H,
      validMask,
      minPix,
      p,
      forceFirst,
      mpp,
      label,
    )
  }

  let res = runWith(1.0, false)
  const firstOk = res?.rows?.[0]?.ok ?? false

  if (!firstOk) {
    const res2 = runWith(0.5, false)
    if (res2?.rows?.[0]?.ok) {
      res = res2
    } else {
      res = runWith(0.25, true)
    }
  }

  return res
}
